import moment from 'moment';
import { Injectable } from '@angular/core';
import { map, mergeMap, switchMap, tap, take, catchError } from 'rxjs/operators';
import { of, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import { extractCrosscheckPayloads } from '@shared/*';
import { StatusType } from '@models/*';
import {
  CoreActionTypes,
  ClearAllNotifications,
  SetOfflineStatus,
  SetLang,
  SetLangSuccess,
  SetLangFail,
  WakeUpServiceWorkers
} from '@appstore/actions';
import { AppState } from '@appstore/registration';
import {
  LoadDriverData,
  LoadDriverDataSuccess,
  SelectReportDay,
  TourActionTypes,
  LoadCrosscheck,
  ChecklistActionTypes,
  SubmitProtocolSuccess,
  AddRequiredTrackedActions,
  ReportShiftActionTypes,
  EndTourStopTracking
} from '../actions';
import { getRunningShift, getCurrentTourStopTracking } from '../selectors';
import { OverviewState } from '../registration';
import { DriverService } from '../../services';
import { NetworkService } from '../../../core/services/network.service';

@Injectable()
export class CombinedOverviewEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<OverviewState | AppState>,
    private readonly networkService: NetworkService,
    private readonly translateService: TranslateService,
    private readonly driverService: DriverService
  ) {}

  initializeDriverData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.INIT_DRIVER_DATA),
      mergeMap(() => {
        const now = moment().startOf('day').toISOString(true);
        return [new LoadDriverData({ date: now, performPreload: true, refreshMsg: false }), new SelectReportDay(now)];
      })
    )
  );

  refreshDriverData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.REFRESH_DRIVER_DATA),
      mergeMap(() => {
        const actionsArray: any[] = [];
        const now = moment().startOf('day').toISOString(true);
        const isOnline = this.networkService.checkOnlineStatus();
        if (isOnline) {
          actionsArray.push(
            new LoadDriverData({ date: now, performPreload: false, refreshMsg: true }),
            new SelectReportDay(now),
            new ClearAllNotifications(),
            new WakeUpServiceWorkers()
          );
        } else {
          actionsArray.push(new SetOfflineStatus(true));
        }

        return actionsArray;
      })
    )
  );

  preloadCrosschecksInitial$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TourActionTypes.LOAD_DRIVER_DATA_SUCCESS),
      map((action: LoadDriverDataSuccess) => action.payload),
      switchMap(({ tours, performPreload }) => {
        let actionsArray: any[] = [];
        if (performPreload) {
          const payloads = extractCrosscheckPayloads(tours);
          actionsArray = payloads.map((elem) => new LoadCrosscheck(elem));
        }

        return actionsArray;
      })
    )
  );

  updateActionAfterProtocol$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActionTypes.SUBMIT_PROTOCOL_SUCCESS),
      map((action: SubmitProtocolSuccess) => action.payload.tourStopActionId),
      map((tourStopActionId) => {
        const tsaUpdates = [
          {
            tourStopActionId,
            status: StatusType.COMPLETED,
            rasterLocation: null
          }
        ];
        return new AddRequiredTrackedActions(tsaUpdates);
      })
    )
  );

  endCurrentTourStopTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportShiftActionTypes.END_CURRENT_TOUR_STOP_TRACKING),
      mergeMap(() =>
        zip(this.store.select(getRunningShift), this.store.select(getCurrentTourStopTracking)).pipe(take(1))
      ),
      map(([_runningShift, runningTourStopTracking]) => {
        const payload = {
          date: moment().toISOString(true),
          tourStopTrackingId: runningTourStopTracking.id,
          previous: true
        };

        return new EndTourStopTracking(payload);
      })
    )
  );

  setLang$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.SET_LANG),
      map((action: SetLang) => action.payload),
      switchMap(({ lang, initial }) => {
        return this.driverService.setDriverLanguage({ language: lang }).pipe(
          map(
            () => new SetLangSuccess({ lang, initial }),
            catchError((error) => of(new SetLangFail({ error })))
          )
        );
      })
    )
  );

  setLangSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.SET_LANG_SUCCESS),
      map((action: SetLangSuccess) => action.payload),
      tap((payload) => this.translateService.use(payload.lang)),
      map(() => {
        const now = moment().startOf('day').toISOString(true);
        return new LoadDriverData({ date: now, performPreload: true, refreshMsg: false });
      })
    )
  );
}
